/**
 * Authentication Flow Utilities
 *
 * General utility functions for authentication flows
 *
 * ORY INTEGRATION: Helper functions for auth flows
 */
import { AuthFlowMethod } from '../types/login-types';
/**
 * Map method to display icon
 */
export function getMethodIcon(method) {
    switch (method) {
        case AuthFlowMethod.EmailOTP:
            return 'Email';
        case AuthFlowMethod.Passkey:
            return 'Fingerprint';
        default:
            return 'Lock';
    }
}
/**
 * Check if method requires user interaction
 *
 * ORY INTEGRATION: Determine if method needs immediate user action
 */
export function requiresUserInteraction(method) {
    switch (method) {
        case AuthFlowMethod.Passkey:
            return true;
        case AuthFlowMethod.EmailOTP:
            return false; // Email is sent first
        default:
            return true;
    }
}
/**
 * Get method display name
 *
 * ORY INTEGRATION: Display names for UI
 */
export function getMethodDisplayName(method) {
    switch (method) {
        case AuthFlowMethod.EmailOTP:
            return 'Email Code';
        case AuthFlowMethod.Passkey:
            return 'Passkey';
        default:
            return 'Unknown Method';
    }
}
/**
 * Get method description
 *
 * ORY INTEGRATION: Help text for users
 */
export function getMethodDescription(method) {
    switch (method) {
        case AuthFlowMethod.EmailOTP:
            return 'We\'ll send a verification code to your email';
        case AuthFlowMethod.Passkey:
            return 'Use your device\'s biometric authentication';
        default:
            return 'Select an authentication method';
    }
}
/**
 * Check if method is passwordless
 *
 * ORY INTEGRATION: Identify passwordless methods
 */
export function isPasswordlessMethod(method) {
    // All our methods are passwordless
    return true;
}
/**
 * Format countdown timer
 *
 * ORY INTEGRATION: Display OTP resend countdown
 */
export function formatCountdown(seconds) {
    if (seconds <= 0)
        return '';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    if (minutes > 0) {
        return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    }
    return `${seconds}s`;
}
/**
 * Get error message for common auth errors
 *
 * ORY INTEGRATION: User-friendly error messages
 */
export function getAuthErrorMessage(error) {
    if (!error)
        return 'An unknown error occurred';
    // Handle Ory error responses
    if (error.response?.data?.ui?.messages) {
        const messages = error.response.data.ui.messages;
        if (messages.length > 0) {
            return messages[0].text;
        }
    }
    // Common error patterns
    const errorString = error.message || error.toString();
    if (errorString.includes('expired')) {
        return 'Your session has expired. Please try again.';
    }
    if (errorString.includes('invalid') || errorString.includes('incorrect')) {
        return 'Invalid code. Please check and try again.';
    }
    if (errorString.includes('network') || errorString.includes('fetch')) {
        return 'Network error. Please check your connection.';
    }
    return errorString || 'Authentication failed. Please try again.';
}
// WebAuthn/Passkey support functions removed - using PasskeyService for capability checks 
